import {
  SIGNALR_BRIDGE_MESSAGES,
  type SignalRBridgeMessage,
} from '@/types/signalRBridge';

export class SignalRBridge {
  private iframe: HTMLIFrameElement;
  private eventHandlers = new Map<string, (...args: unknown[]) => void>();
  private pendingInvokes = new Map<
    string,
    { resolve: (value: unknown) => void; reject: (error: Error) => void }
  >();
  private onStateChange?: (state: string) => void;
  private isReady = false;
  private messageQueue: SignalRBridgeMessage[] = [];

  constructor() {
    this.iframe = document.createElement('iframe');
    this.iframe.src = `${import.meta.env.PROD ? import.meta.env.VITE_WIDGET_DOMAIN : ''}/signalr-bridge.html`;
    this.iframe.style.display = 'none';
    this.iframe.style.width = '0';
    this.iframe.style.height = '0';
    this.iframe.style.border = 'none';

    document.body.appendChild(this.iframe);
    window.addEventListener('message', this.handleMessage.bind(this));
  }

  private handleMessage = (event: MessageEvent): void => {
    // Basic origin validation - you might want to make this more specific
    if (!event.data || typeof event.data !== 'object') {
      return;
    }

    const message: SignalRBridgeMessage = event.data;
    const { type, method, args, error, id, result } = message;

    switch (type) {
      case SIGNALR_BRIDGE_MESSAGES.READY:
        this.isReady = true;
        this.processMessageQueue();
        break;

      case SIGNALR_BRIDGE_MESSAGES.CONNECTED:
      case SIGNALR_BRIDGE_MESSAGES.DISCONNECTED:
      case SIGNALR_BRIDGE_MESSAGES.RECONNECTING:
        this.onStateChange?.(type);
        break;

      case SIGNALR_BRIDGE_MESSAGES.MESSAGE:
        if (method && args) {
          this.eventHandlers.get(method)?.(...args);
        }
        break;

      case SIGNALR_BRIDGE_MESSAGES.INVOKE_RESULT:
        if (id) {
          this.pendingInvokes.get(id)?.resolve(result);
          this.pendingInvokes.delete(id);
        }
        break;

      case SIGNALR_BRIDGE_MESSAGES.INVOKE_ERROR:
        if (id) {
          this.pendingInvokes
            .get(id)
            ?.reject(new Error(error || 'Invoke failed'));
          this.pendingInvokes.delete(id);
        }
        break;

      case SIGNALR_BRIDGE_MESSAGES.ERROR:
        console.error('SignalR Bridge Error:', error);
        break;
    }
  };

  private processMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift()!;
      this.postMessage(message);
    }
  }

  private postMessage(message: SignalRBridgeMessage): void {
    if (this.isReady && this.iframe.contentWindow) {
      this.iframe.contentWindow.postMessage(message, '*');
    } else {
      this.messageQueue.push(message);
    }
  }

  connect(url: string, location: string, groupName: string): void {
    this.postMessage({
      type: SIGNALR_BRIDGE_MESSAGES.CONNECT,
      data: { url, location, groupName },
    });
  }

  on(method: string, callback: (...args: unknown[]) => void): void {
    this.eventHandlers.set(method, callback);
    this.postMessage({
      type: SIGNALR_BRIDGE_MESSAGES.SUBSCRIBE,
      data: { method },
    });
  }

  off(method: string): void {
    this.eventHandlers.delete(method);
    this.postMessage({
      type: SIGNALR_BRIDGE_MESSAGES.UNSUBSCRIBE,
      data: { method },
    });
  }

  async invoke(method: string, ...args: unknown[]): Promise<unknown> {
    const id = Math.random().toString(36).substr(2, 9);

    return new Promise((resolve, reject) => {
      this.pendingInvokes.set(id, { resolve, reject });
      this.postMessage({
        type: SIGNALR_BRIDGE_MESSAGES.INVOKE,
        data: { method, args, id },
      });
    });
  }

  onConnectionStateChanged(callback: (state: string) => void): void {
    this.onStateChange = callback;
  }

  destroy(): void {
    if (this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }
    window.removeEventListener('message', this.handleMessage);
    this.eventHandlers.clear();
    this.pendingInvokes.clear();
  }
}
